import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useRoles } from "../lib/useRoles";
import { useTechIdentity } from "../lib/TechIdentityContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { getIPSummary } from '../lib/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import './IPReporting.css';

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
const TIER_COLORS = {
  exceptional: '#4F8B4A',
  satisfactory: '#8FB5C2',
  needs_improvement: '#D97706',
};

const COMPONENT_KEYS = ['availability', 'communication', 'documentation', 'eventHandling'];
const COMPONENT_LABELS = {
  availability: 'Availability',
  communication: 'Communication',
  documentation: 'Documentation',
  eventHandling: 'Event Handling',
};

const ROWS_PER_PAGE = 25;
const BAR_HEIGHT_PX = 24;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function getCurrentQuarter() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return `${now.getFullYear()} Q${q}`;
}

function generateQuarterOptions() {
  const options = [];
  const now = new Date();
  let year = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) + 1;
  for (let i = 0; i < 8; i++) {
    options.push(`${year} Q${q}`);
    q -= 1;
    if (q < 1) {
      q = 4;
      year -= 1;
    }
  }
  return options;
}

function quarterToDateRange(quarterStr) {
  const match = quarterStr.match(/^(\d{4})\s+Q([1-4])$/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const q = parseInt(match[2], 10);
  const startMonth = (q - 1) * 3;
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, startMonth + 3, 0);
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
}

function formatDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function tierLabel(tier) {
  if (tier === 'exceptional') return 'Exceptional';
  if (tier === 'satisfactory') return 'Satisfactory';
  return 'Needs Improvement';
}

function formatNumber(n, digits = 1) {
  if (n === null || n === undefined) return '—';
  return Number(n).toFixed(digits);
}

function formatPercent(n) {
  if (n === null || n === undefined) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function csvEscape(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCSV(details, period) {
  const headers = [
    'IP', 'Month', '% of Cases',
    'Component', 'Score', 'Possible Score', 'Component Score',
  ];
  const rows = [headers.join(',')];
  details.forEach((row) => {
    const total = row.components.total;
    rows.push([
      csvEscape(row.ip),
      csvEscape(row.monthDisplay),
      csvEscape(formatNumber(row.pctOfCases, 1)),
      'Total',
      csvEscape(formatNumber(total.score)),
      csvEscape(formatNumber(total.max)),
      csvEscape(formatNumber(total.pct)),
    ].join(','));
    COMPONENT_KEYS.forEach((k) => {
      const c = row.components[k];
      if (!c) return;
      rows.push([
        '', '', '',
        COMPONENT_LABELS[k],
        csvEscape(formatNumber(c.score)),
        csvEscape(formatNumber(c.max)),
        csvEscape(formatNumber(c.pct)),
      ].join(','));
    });
  });
  const header = `IP Reports Export\nPeriod: ${period.startDate} to ${period.endDate}\nGenerated: ${new Date().toISOString()}\n\n`;
  return header + rows.join('\n');
}

function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function IPReporting({ isAdmin }) {
  // An IP sees their own figures. The API already replaces any reader filter
  // they send with their own id, so this only removes a control that would
  // appear to do something and then not.
  const { isIP } = useRoles();
  const { effectiveReaderId } = useTechIdentity();
  const quarterOptions = useMemo(() => generateQuarterOptions(), []);
  const [filterMode, setFilterMode] = useState('quarter');
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [viewMode, setViewMode] = useState('both'); // 'both' | 'chart' | 'table'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIP, setSelectedIP] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedReaders, setSelectedReaders] = useState([]); // reader1_id strings
  const [readerPickerOpen, setReaderPickerOpen] = useState(false);
  // Hospital names. Filters which IPs appear, not which of their cases count.
  const [selectedHospitals, setSelectedHospitals] = useState([]);
  const [hospitalPickerOpen, setHospitalPickerOpen] = useState(false);
  // Reader groups (set on Users > Readers). "__unassigned__" = readers with no group.
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const groupPickerRef = useRef(null);
  const exportMenuRef = useRef(null);
  const readerPickerRef = useRef(null);
  const hospitalPickerRef = useRef(null);

  // Initialize custom date range with current quarter on first mount
  useEffect(() => {
    const range = quarterToDateRange(getCurrentQuarter());
    if (range) {
      setCustomStartDate(range.startDate);
      setCustomEndDate(range.endDate);
    }
  }, []);

  // Resolve active date range
  const activeRange = useMemo(() => {
    if (filterMode === 'quarter') return quarterToDateRange(selectedQuarter);
    if (customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    return null;
  }, [filterMode, selectedQuarter, customStartDate, customEndDate]);

  // On first mount, if the current quarter has no cases, fall back to the most
  // recent quarter that does (probe backward, max 4).
  const autoQuarterRef = useRef(false);
  useEffect(() => {
    if ((!isAdmin && !isIP) || autoQuarterRef.current || filterMode !== 'quarter') return;
    autoQuarterRef.current = true;
    let cancelled = false;
    (async () => {
      const start = quarterOptions.indexOf(getCurrentQuarter());
      if (start < 0) return;
      for (let i = start; i < Math.min(start + 4, quarterOptions.length); i++) {
        const r = quarterToDateRange(quarterOptions[i]);
        if (!r) continue;
        try {
          const res = await getIPSummary(r.startDate, r.endDate, []);
          if (cancelled) return;
          if (res?.summary?.totalCases > 0) {
            if (i !== start) setSelectedQuarter(quarterOptions[i]);
            return;
          }
        } catch { return; }
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, isIP, quarterOptions, filterMode]);

  // Sorted-join key so the dep array sees a stable string across array identity changes.
  const readersDepKey = useMemo(
    () => [...selectedReaders].sort().join(','),
    [selectedReaders]
  );
  const hospitalsDepKey = useMemo(
    () => [...selectedHospitals].sort().join('|'),
    [selectedHospitals]
  );
  const groupsDepKey = useMemo(
    () => [...selectedGroups].sort().join('|'),
    [selectedGroups]
  );

  // Fetch data when range or reader filter changes
  useEffect(() => {
    if (!isAdmin && !isIP) return;
    if (!activeRange) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelectedIP(null);
    setPage(0);
    getIPSummary(activeRange.startDate, activeRange.endDate, selectedReaders, selectedHospitals, selectedGroups, effectiveReaderId)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load report');
          setData(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeRange?.startDate, activeRange?.endDate, isAdmin, isIP, readersDepKey, hospitalsDepKey, groupsDepKey, effectiveReaderId]);

  // Close export menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    }
    if (exportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [exportMenuOpen]);

  // Close reader picker on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (readerPickerRef.current && !readerPickerRef.current.contains(e.target)) {
        setReaderPickerOpen(false);
      }
    }
    if (readerPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [readerPickerOpen]);

  // Close hospital picker on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (hospitalPickerRef.current && !hospitalPickerRef.current.contains(e.target)) {
        setHospitalPickerOpen(false);
      }
    }
    if (hospitalPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [hospitalPickerOpen]);

  // Close group picker on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (groupPickerRef.current && !groupPickerRef.current.contains(e.target)) {
        setGroupPickerOpen(false);
      }
    }
    if (groupPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [groupPickerOpen]);

  const availableGroups = useMemo(
    () => (data?.availableGroups || []).map((g) => ({
      id: g.name,
      name: g.name === '__unassigned__' ? 'Unassigned' : g.name,
      caseCount: g.caseCount,
    })),
    [data]
  );

  const availableHospitals = useMemo(
    () => (data?.availableHospitals || []).map((h) => ({ id: h.name, name: h.name, caseCount: h.caseCount })),
    [data]
  );

  // Filter details by selected IP (drill-down)
  const filteredDetails = useMemo(() => {
    if (!data?.details) return [];
    if (!selectedIP) return data.details;
    return data.details.filter((d) => d.ip === selectedIP);
  }, [data, selectedIP]);

  const totalPages = Math.max(1, Math.ceil(filteredDetails.length / ROWS_PER_PAGE));
  const paginatedDetails = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return filteredDetails.slice(start, start + ROWS_PER_PAGE);
  }, [filteredDetails, page]);

  // Handlers
  const handleBarClick = useCallback((entry) => {
    if (!entry || !entry.ip) return;
    setSelectedIP((prev) => (prev === entry.ip ? null : entry.ip));
    setPage(0);
  }, []);

  const handleClearFilter = useCallback(() => {
    setSelectedIP(null);
    setPage(0);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!data) return;
    const csv = buildCSV(filteredDetails, data.period);
    const filename = `ip-reporting-${data.period.startDate}-to-${data.period.endDate}.csv`;
    downloadCSV(csv, filename);
    setExportMenuOpen(false);
  }, [data, filteredDetails]);

  const buildTableBody = useCallback(() => {
    const body = [];
    filteredDetails.forEach((row) => {
      const total = row.components.total;
      body.push([
        row.ip,
        row.monthDisplay,
        `${formatNumber(row.pctOfCases, 1)}%`,
        'Total',
        formatNumber(total.score),
        formatNumber(total.max),
        formatPercent(total.pct),
      ]);
      COMPONENT_KEYS.forEach((k) => {
        const c = row.components[k];
        if (!c) return;
        body.push([
          '', '', '',
          COMPONENT_LABELS[k],
          formatNumber(c.score),
          formatNumber(c.max),
          formatPercent(c.pct),
        ]);
      });
    });
    return body;
  }, [filteredDetails]);

  const handleExportPDF = useCallback(async () => {
    if (!data) return;
    setExportMenuOpen(false);
    setExporting(true);

    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

      // ─── Page 1: Cover/summary ───
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.text('IP Reports', 40, 40);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Period: ${data.period.startDate} to ${data.period.endDate}`, 40, 58);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 72);
      if (selectedIP) doc.text(`Filtered to: ${selectedIP}`, 40, 86);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text('Summary', 40, selectedIP ? 110 : 100);
      const summaryY = selectedIP ? 128 : 118;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Total Cases: ${data.summary.totalCases.toLocaleString()}`, 40, summaryY);
      doc.text(`Avg Total Report Score: ${formatPercent(data.summary.avgTotalReportScore)}`, 220, summaryY);
      doc.text(`Interpreting Physicians: ${data.summary.totalIPs}`, 460, summaryY);
      doc.text(`Below 95% Threshold: ${data.summary.belowThreshold}`, 660, summaryY);

      console.log('CHART:', { viewMode, chartEl: !!document.getElementById('ipr-chart-capture') });

      // ─── Chart page (only if chart is in view) ───
      if (viewMode !== 'table') {
        const chartEl = document.getElementById('ipr-chart-capture');
        if (chartEl) {
          try {
            const canvas = await html2canvas(chartEl, {
              scale: 2,
              backgroundColor: '#FFFFFF',
              logging: false,
              windowHeight: chartEl.scrollHeight,
              height: chartEl.scrollHeight,
            });
            const imgData = canvas.toDataURL('image/png');
            const titleY = selectedIP ? 160 : 150;
            const imageTop = titleY + 20;
            const bottomMargin = 40;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const availWidth = pageWidth - 80;
            const availHeight = pageHeight - imageTop - bottomMargin;
            const scaleFactor = Math.min(availWidth / canvas.width, availHeight / canvas.height);
            const finalWidth = canvas.width * scaleFactor;
            const finalHeight = canvas.height * scaleFactor;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(15, 23, 42);
            doc.text('Avg. Total Report Score by Interpreting Physician', 40, titleY);
            doc.addImage(imgData, 'PNG', 40, imageTop, finalWidth, finalHeight);
          } catch (e) {
            console.warn('Chart capture failed; PDF will not include chart image.', e);
          }
        }
      }

      // ─── Details table pages (only if table is in view) ───
      if (viewMode !== 'chart') {
        // Only start a fresh page if the chart already used this one.
        const chartRendered = viewMode !== 'table';
        if (chartRendered) doc.addPage();
        const detailsTop = chartRendered ? 30 : (selectedIP ? 160 : 150);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(15, 23, 42);
        doc.text('Details', 40, detailsTop);

        console.log('DETAILS: page', doc.internal.getNumberOfPages());

        autoTable(doc, {
          startY: detailsTop + 20,
          head: [['IP', 'Month', '% Cases', 'Component', 'Score', 'Possible', 'Score %']],
          body: buildTableBody(),
          styles: { fontSize: 8, cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.5 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            4: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' },
          },
          margin: { left: 40, right: 40 },
        });
      }

      const filename = `ip-reporting-${data.period.startDate}-to-${data.period.endDate}.pdf`;
      doc.save(filename);
    } finally {
      setExporting(false);
    }
  }, [data, selectedIP, viewMode, buildTableBody]);

  // Admins see every reader; an IP sees themselves, scoped by the API. Anyone
  // else has no business here.
  if (!isAdmin && !isIP) {
    return (
      <div className="ipr-access-denied">
        <h2>Access Denied</h2>
        <p>IP Reports requires admin privileges.</p>
      </div>
    );
  }

  const chartData = data?.chart || [];
  // One reader is one bar, so there is nothing to compare and nothing to
  // click. The details table carries their figures on its own.
  const showChart = !isIP && viewMode !== 'table';
  const showTable = viewMode !== 'chart';

  return (
    <div className="ipr-root">
      {/* ─────────── Top header ─────────── */}
      <div className="ipr-topbar">
        <div>
          <div className="ipr-topbar-title">{isIP ? "My IP Report" : "IP Reports"}</div>
          <div className="ipr-topbar-subtitle">
            Interpreting Physician quality assurance summary
          </div>
        </div>
      </div>

      {/* ─────────── Filter bar ─────────── */}
      <div className="ipr-filter-bar">
        <div className="ipr-toggle-pill">
          <button
            className={filterMode === 'quarter' ? 'active' : ''}
            onClick={() => setFilterMode('quarter')}
            type="button"
          >
            Year Quarter
          </button>
          <button
            className={filterMode === 'range' ? 'active' : ''}
            onClick={() => setFilterMode('range')}
            type="button"
          >
            Custom Range
          </button>
        </div>

        {filterMode === 'quarter' && (
          <div className="ipr-filter-group">
            <span className="ipr-filter-label">Period</span>
            <select
              className="ipr-filter-control"
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
            >
              {quarterOptions.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>
        )}

        {filterMode === 'range' && (
          <div className="ipr-filter-group">
            <span className="ipr-filter-label">From</span>
            <input
              type="date"
              className="ipr-filter-control"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
            />
            <span className="ipr-filter-label">To</span>
            <input
              type="date"
              className="ipr-filter-control"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
            />
          </div>
        )}

        {/* View mode toggle. Nothing to toggle between without the chart. */}
        {!isIP && (
        <div className="ipr-filter-group">
          <span className="ipr-filter-label">View</span>
          <div className="ipr-toggle-pill">
            <button
              className={viewMode === 'both' ? 'active' : ''}
              onClick={() => setViewMode('both')}
              type="button"
              title="Show chart and table"
            >
              Both
            </button>
            <button
              className={viewMode === 'chart' ? 'active' : ''}
              onClick={() => setViewMode('chart')}
              type="button"
              title="Show only the chart"
            >
              Chart
            </button>
            <button
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
              type="button"
              title="Show only the table"
            >
              Table
            </button>
          </div>
        </div>
        )}

        {/* Reader filter. Not shown to an IP: there is only ever one reader. */}
        {!isIP && (
        <div className="ipr-filter-group" ref={readerPickerRef} style={{ position: 'relative' }}>
          <span className="ipr-filter-label">Readers</span>
          <button
            type="button"
            className="ipr-filter-control"
            onClick={() => setReaderPickerOpen((v) => !v)}
            style={{ cursor: 'pointer', minWidth: 160, textAlign: 'left' }}
          >
            {selectedReaders.length === 0 ? 'All readers' : `${selectedReaders.length} selected`}
            <span style={{ opacity: 0.6, marginLeft: 6 }}>▾</span>
          </button>
          {readerPickerOpen && (
            <ReaderPicker
              available={data?.availableReaders || []}
              selected={selectedReaders}
              onChange={setSelectedReaders}
              onClose={() => setReaderPickerOpen(false)}
            />
          )}
        </div>
        )}

        {/* Hospital filter: shows IPs who read a case there that month */}
        {!isIP && (
        <div className="ipr-filter-group" ref={hospitalPickerRef} style={{ position: 'relative' }}>
          <span className="ipr-filter-label">Hospitals</span>
          <button
            type="button"
            className="ipr-filter-control"
            onClick={() => setHospitalPickerOpen((v) => !v)}
            style={{ cursor: 'pointer', minWidth: 160, textAlign: 'left' }}
            title="Shows IPs who had a case at the selected hospital during the month. All of their cases that month are included."
          >
            {selectedHospitals.length === 0 ? 'All hospitals' : `${selectedHospitals.length} selected`}
            <span style={{ opacity: 0.6, marginLeft: 6 }}>▾</span>
          </button>
          {hospitalPickerOpen && (
            <ReaderPicker
              available={availableHospitals}
              selected={selectedHospitals}
              onChange={setSelectedHospitals}
              onClose={() => setHospitalPickerOpen(false)}
              noun="hospitals"
            />
          )}
        </div>

        )}

        {/* Group filter: reader groups from Users > Readers */}
        {!isIP && (
        <div className="ipr-filter-group" ref={groupPickerRef} style={{ position: 'relative' }}>
          <span className="ipr-filter-label">Groups</span>
          <button
            type="button"
            className="ipr-filter-control"
            onClick={() => setGroupPickerOpen((v) => !v)}
            style={{ cursor: 'pointer', minWidth: 140, textAlign: 'left' }}
            title="Reader groups are set on the Users page, Readers tab."
          >
            {selectedGroups.length === 0 ? 'All groups' : `${selectedGroups.length} selected`}
            <span style={{ opacity: 0.6, marginLeft: 6 }}>▾</span>
          </button>
          {groupPickerOpen && (
            <ReaderPicker
              available={availableGroups}
              selected={selectedGroups}
              onChange={setSelectedGroups}
              onClose={() => setGroupPickerOpen(false)}
              noun="groups"
            />
          )}
        </div>
        )}

        <div className="ipr-spacer" />

        {/* Export is a reporting tool: a physician reads their own figures
            rather than extracting a dataset from them. */}
        {!isIP && (
        <div className="ipr-export-wrap" ref={exportMenuRef}>
          <button
            className="ipr-btn ipr-btn-primary"
            onClick={() => setExportMenuOpen((v) => !v)}
            disabled={!data || loading || exporting}
            type="button"
          >
            {exporting ? 'Exporting…' : '⬇ Export'}{' '}
            <span style={{ opacity: 0.7, marginLeft: 2 }}>▾</span>
          </button>
          {exportMenuOpen && (
            <div className="ipr-export-menu">
              <div className="ipr-export-menu-item" onClick={handleExportCSV}>
                <span>📄</span>
                <span>Export as CSV</span>
                <span className="ipr-export-meta">.csv</span>
              </div>
              <div className="ipr-export-menu-item" onClick={handleExportPDF}>
                <span>📑</span>
                <span>Export as PDF</span>
                <span className="ipr-export-meta">.pdf</span>
              </div>
              <div className="ipr-export-hint">Exports reflect the current view</div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* ─────────── Drill-down pill ─────────── */}
      {selectedIP && (
        <div className="ipr-active-filters">
          <span>Filtered to: <strong>{selectedIP}</strong></span>
          <button className="ipr-clear-filter" onClick={handleClearFilter} type="button">
            Clear filter
          </button>
        </div>
      )}

      {/* ─────────── Content ─────────── */}
      <div className="ipr-content">
        {loading && <div className="ipr-loading">Loading report…</div>}
        {error && (
          <div className="ipr-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* KPI Row — always visible */}
            <div className={`ipr-kpi-row${isIP ? " ipr-kpi-row--own" : ""}`}>
              <KPICard
                label="Total Cases"
                value={data.summary.totalCases.toLocaleString()}
                tone="neutral"
              />
              <KPICard
                label="Avg. Total Report Score"
                value={formatPercent(data.summary.avgTotalReportScore)}
                tone="neutral"
              />
              {!isIP && (
                <KPICard
                  label="Interpreting Physicians"
                  value={data.summary.totalIPs.toString()}
                  tone="neutral"
                />
              )}
              {/* An IP counts their own cases below the threshold; the admin
                  view counts readers, which is what it is comparing. */}
              {isIP ? (
                <KPICard
                  label="Cases Below 95%"
                  value={(data.summary.casesBelowThreshold ?? 0).toString()}
                  tone={(data.summary.casesBelowThreshold ?? 0) > 0 ? 'warning' : 'neutral'}
                  hint={
                    data.summary.totalCases > 0
                      ? `${formatPercent(
                          ((data.summary.casesBelowThreshold ?? 0) / data.summary.totalCases) * 100
                        )} of your cases`
                      : 'No cases this period'
                  }
                />
              ) : (
                <KPICard
                  label="Below 95% Threshold"
                  value={data.summary.belowThreshold.toString()}
                  tone={data.summary.belowThreshold > 0 ? 'warning' : 'neutral'}
                  hint="IPs flagged for review"
                />
              )}
            </div>

            {/* Bar Chart Card */}
            {showChart && (
              <div className="ipr-card">
                <div className="ipr-card-header">
                  <div>
                    <div className="ipr-card-title">
                      Avg. Total Report Score by Interpreting Physician
                    </div>
                    <div className="ipr-card-subtitle">
                      Click any bar to filter the details table below
                    </div>
                  </div>
                  <ChartLegend />
                </div>
                <div className="ipr-card-body">
                  {chartData.length === 0 ? (
                    <div className="ipr-empty">No IP data for this period.</div>
                  ) : viewMode === 'chart' ? (
                    <IPBarChart
                      data={chartData}
                      onBarClick={handleBarClick}
                      selectedIP={selectedIP}
                    />
                  ) : (
                    <div className="ipr-chart-scroll">
                      <IPBarChart
                        data={chartData}
                        onBarClick={handleBarClick}
                        selectedIP={selectedIP}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Details Table Card */}
            {showTable && (
              <div className="ipr-card">
                <div className="ipr-card-header">
                  <div>
                    <div className="ipr-card-title">Details</div>
                    <div className="ipr-card-subtitle">
                      {selectedIP ? (
                        <>Filtered to {selectedIP} · {filteredDetails.length} entries</>
                      ) : (
                        <>
                          {!isIP && (
                            <>{data.summary.totalIPs} Interpreting Physicians ·{' '}</>
                          )}
                          {data.summary.totalCases.toLocaleString()} cases ·{' '}
                          {data.period.startDate} to {data.period.endDate}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ipr-table-wrap">
                  <DetailsTable rows={paginatedDetails} />
                </div>
                <div className="ipr-table-footer">
                  <span>
                    Showing {filteredDetails.length === 0 ? 0 : page * ROWS_PER_PAGE + 1}
                    {'–'}
                    {Math.min((page + 1) * ROWS_PER_PAGE, filteredDetails.length)} of{' '}
                    <strong>{filteredDetails.length}</strong> rows
                  </span>
                  {totalPages > 1 && (
                    <Pagination
                      page={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────
function KPICard({ label, value, tone, hint }) {
  const valueClass = tone === 'warning' ? 'ipr-kpi-value warning' : 'ipr-kpi-value';
  return (
    <div className="ipr-kpi">
      <div className="ipr-kpi-label">{label}</div>
      <div className={valueClass}>{value}</div>
      {hint && <div className="ipr-kpi-hint">{hint}</div>}
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="ipr-legend">
      <div className="ipr-legend-item">
        <span className="ipr-legend-swatch" style={{ background: TIER_COLORS.exceptional }} />
        <span>Exceptional (≥95%)</span>
      </div>
      <div className="ipr-legend-item">
        <span className="ipr-legend-swatch" style={{ background: TIER_COLORS.satisfactory }} />
        <span>Satisfactory (85–94.9%)</span>
      </div>
      <div className="ipr-legend-item">
        <span className="ipr-legend-swatch" style={{ background: TIER_COLORS.needs_improvement }} />
        <span>Needs Improvement (&lt;85%)</span>
      </div>
    </div>
  );
}

function IPBarChart({ data, onBarClick, selectedIP }) {
  const height = Math.max(300, data.length * BAR_HEIGHT_PX + 50);

  return (
    <div
      id="ipr-chart-capture"
      style={{ width: '100%', height: `${height}px`, background: '#FFFFFF' }}
    >
      <ResponsiveContainer>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 10, right: 80, bottom: 20, left: 140 }}
          onClick={(e) => {
            if (e && e.activeLabel) onBarClick({ ip: e.activeLabel });
          }}
        >
          <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fill: '#94A3B8', fontSize: 11 }}
            axisLine={{ stroke: '#CBD5E1' }}
          />
          <YAxis
            type="category"
            dataKey="ip"
            tick={{ fill: '#0F172A', fontSize: 12 }}
            axisLine={{ stroke: '#CBD5E1' }}
            width={140}
          />
          <Tooltip
            cursor={{ fill: '#F1F5F9' }}
            formatter={(value, name, props) => [
              `${formatNumber(value)}% (${props.payload.caseCount} case${props.payload.caseCount === 1 ? '' : 's'})`,
              tierLabel(props.payload.tier),
            ]}
          />
          <Bar dataKey="avgScore" cursor="pointer" radius={[0, 3, 3, 0]}>
            {data.map((entry, idx) => (
              <Cell
                key={`cell-${idx}`}
                fill={TIER_COLORS[entry.tier] || '#94A3B8'}
                opacity={selectedIP && selectedIP !== entry.ip ? 0.35 : 1}
              />
            ))}
            <LabelList
              dataKey="avgScore"
              position="right"
              formatter={(v) => `${formatNumber(v)}%`}
              style={{ fill: '#0F172A', fontSize: 11, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DetailsTable({ rows }) {
  if (rows.length === 0) {
    return <div className="ipr-empty">No detail rows to display.</div>;
  }
  return (
    <table className="ipr-details-table">
      <thead>
        <tr>
          <th>IP</th>
          <th>Month of Surgery</th>
          <th className="num">% of Cases</th>
          <th>IP QA Component</th>
          <th className="num">Score</th>
          <th className="num">Possible Score</th>
          <th className="num">Total Report Score</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIdx) => {
          const total = row.components.total;
          const subRows = COMPONENT_KEYS.map((k) => ({
            key: k,
            label: COMPONENT_LABELS[k],
            data: row.components[k],
          })).filter((sr) => sr.data);

          return (
            <React.Fragment key={`${row.ip}-${row.month}-${rowIdx}`}>
              <tr className="ipr-total-row">
                <td>{row.ip}</td>
                <td className="dim">{row.monthDisplay}</td>
                <td className="num dim">{formatNumber(row.pctOfCases, 1)}%</td>
                <td>Total</td>
                <td className="num">{formatNumber(total.score)}</td>
                <td className="num dim">{formatNumber(total.max)}</td>
                <td className="num">
                  <span className={`ipr-score-pct ${tierClassForPct(total.pct)}`}>
                    {formatPercent(total.pct)}
                  </span>
                </td>
              </tr>
              {subRows.map((sr) => (
                <tr key={`${row.ip}-${row.month}-${sr.key}`} className="ipr-component-row">
                  <td />
                  <td />
                  <td />
                  <td>{sr.label}</td>
                  <td className="num">{formatNumber(sr.data.score)}</td>
                  <td className="num dim">{formatNumber(sr.data.max)}</td>
                  <td className="num">
                    <span className={`ipr-score-pct ${tierClassForPct(sr.data.pct)}`}>
                      {formatPercent(sr.data.pct)}
                    </span>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function ReaderPicker({ available, selected, onChange, onClose, noun = 'readers' }) {
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((r) => (r.name || '').toLowerCase().includes(q));
  }, [available, query]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(selected.filter((s) => s !== id));
    else onChange([...selected, id]);
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        width: 360,
        maxHeight: 360,
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 6,
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: 8, borderBottom: '1px solid #F1F5F9' }}>
        <input
          type="text"
          placeholder={`Search ${noun}…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #CBD5E1',
            borderRadius: 4,
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        />
      </div>
      <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
        {filtered.length === 0 && (
          <div style={{ padding: 12, color: '#94A3B8', fontSize: 13, textAlign: 'center' }}>
            No matching {noun}
          </div>
        )}
        {filtered.map((r) => {
          const isOn = selectedSet.has(r.id);
          return (
            <div
              key={r.id}
              onClick={() => toggle(r.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr auto',
                columnGap: 8,
                alignItems: 'center',
                padding: '6px 8px',
                cursor: 'pointer',
                borderRadius: 4,
                background: isOn ? '#EFF6FF' : 'transparent',
                fontSize: 13,
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <input
                type="checkbox"
                checked={isOn}
                readOnly
                style={{ margin: 0, width: 16, height: 16, pointerEvents: 'none' }}
              />
              <span
                title={r.name}
                style={{
                  color: '#0F172A',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.name}
              </span>
              <span style={{ color: '#94A3B8', fontSize: 12 }}>{r.caseCount}</span>
            </div>
          );
        })}
      </div>
      <div
        style={{
          padding: 8,
          borderTop: '1px solid #F1F5F9',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={() => onChange([])}
          disabled={selected.length === 0}
          style={{
            background: 'transparent',
            border: 'none',
            color: selected.length === 0 ? '#CBD5E1' : '#2563EB',
            cursor: selected.length === 0 ? 'default' : 'pointer',
            fontSize: 13,
            padding: '4px 8px',
          }}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: '#2563EB',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 4,
            padding: '4px 12px',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}

function tierClassForPct(pct) {
  if (pct === null || pct === undefined) return '';
  if (pct >= 95) return 'tier-exceptional';
  if (pct >= 85) return 'tier-satisfactory';
  return 'tier-needs-imp';
}

function Pagination({ page, totalPages, onPageChange }) {
  const pages = [];
  const maxButtons = 7;
  let start = Math.max(0, page - 3);
  let end = Math.min(totalPages, start + maxButtons);
  start = Math.max(0, end - maxButtons);
  for (let i = start; i < end; i++) pages.push(i);

  return (
    <div className="ipr-pagination">
      <button
        className="ipr-page-btn"
        onClick={() => onPageChange(Math.max(0, page - 1))}
        disabled={page === 0}
        type="button"
      >
        ‹
      </button>
      {pages.map((p) => (
        <button
          key={p}
          className={`ipr-page-btn ${p === page ? 'active' : ''}`}
          onClick={() => onPageChange(p)}
          type="button"
        >
          {p + 1}
        </button>
      ))}
      <button
        className="ipr-page-btn"
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
        type="button"
      >
        ›
      </button>
    </div>
  );
}